import React, { useEffect, useState } from 'react';
import { getApprovedComments, createComment } from '../services/api';
import { MessageCircle, Send, User as UserIcon } from 'lucide-react';

const fmt = (d) =>
  d ? new Date(d).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

const BlogComments = ({ postSlug }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const load = () => {
    setLoading(true);
    getApprovedComments(postSlug)
      .then((r) => setComments(r.data || []))
      .catch(() => setComments([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (postSlug) load();
  }, [postSlug]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Tüm alanlar zorunludur.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await createComment({ ...form, postSlug });
      setSuccessMessage(res.data?.message || 'Yorumunuz alındı.');
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Yorum gönderilemedi.');
    } finally {
      setSubmitting(false);
    }
  };

  const inp =
    'w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843]';

  return (
    <section className="mt-16 border-t border-gray-200 pt-10" data-testid="blog-comments">
      <h2 className="text-2xl font-bold text-[#1B1B1B] mb-6 flex items-center gap-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>
        <MessageCircle size={22} className="text-[#3D4A26]" />
        Yorumlar ({comments.length})
      </h2>

      {/* Comment list */}
      {loading ? (
        <p className="text-sm text-gray-500">Yükleniyor...</p>
      ) : comments.length === 0 ? (
        <p className="text-sm text-gray-500 mb-8">Henüz yorum yok. İlk yorumu siz yapın.</p>
      ) : (
        <ul className="space-y-6 mb-10">
          {comments.map((c) => (
            <li key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
              <div className="w-10 h-10 bg-[#3D4A26] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                {c.name?.charAt(0)?.toUpperCase() || 'A'}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-sm text-[#1B1B1B]">{c.name}</p>
                  <span className="text-xs text-gray-400">•</span>
                  <p className="text-xs text-gray-500">{fmt(c.created_at)}</p>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed mt-1 whitespace-pre-line">{c.message}</p>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Comment form */}
      <div className="bg-[#FAFAF7] border border-gray-200 rounded-xl p-6">
        <h3 className="font-bold text-[#1B1B1B] mb-1">Yorumunuzu bırakın</h3>
        <p className="text-xs text-gray-500 mb-4">Yorumunuz moderasyon sonrası yayınlanacaktır. E-postanız yayınlanmaz.</p>

        <form onSubmit={submit} className="space-y-3" data-testid="comment-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1">Adınız</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} maxLength={80} required data-testid="comment-name" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">E-posta (gizli)</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} maxLength={200} required data-testid="comment-email" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">Yorumunuz</label>
            <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} className={`${inp} resize-none`} maxLength={2000} required data-testid="comment-message" placeholder="Deneyiminizi, sorunuzu veya önerinizi yazın..." />
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}
          {successMessage && <p className="text-sm text-[#3D4A26] bg-[#f0f4d8] border border-[#C8D843]/30 rounded-lg px-3 py-2" data-testid="comment-success">{successMessage}</p>}

          <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 bg-[#3D4A26] text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-[#2d3a1a] transition-colors disabled:opacity-50" data-testid="comment-submit">
            {submitting ? 'Gönderiliyor...' : (<><Send size={14} /> Yorumu Gönder</>)}
          </button>
        </form>
      </div>
    </section>
  );
};

export default BlogComments;
